import React, { useEffect, useState } from 'react';
import * as Yup from "yup";
import Breadcrumbs from '../../components/BreadCrumbs';
import { postOrder } from '../../services/orders';
import { toast } from 'react-toastify';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import Button from '../../components/buttons/Button';
import InputField from '../../components/inputFields/InputField';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import ImagePicker from '../../components/inputFields/ImagePicker';
import { patchManufacturer, postManufacturer } from '../../services/manufacturer';
import { sanitizeMobile } from '../../utils/phone';

function ManufacturerForm() {
    const location = useLocation();
    const navigate = useNavigate();
    const [rowData, setRowData] = useState(null);
    const [image, setImage] = useState(null);
    const [banner, setBanner] = useState(null);

    const handleImageSelect = (file) => {
        setImage(file);
    };

    const handleBannerSelect = (file) => {
        setBanner(file);
    };

    useEffect(() => {
        if (location.state) {
            setRowData(location.state);
            if (location.state.image) {
                setImage(location.state.image);
            }
            if (location.state.banner) {
                setBanner(location.state.banner);
            }
        }
    }, [location]);

    const handleDiscardChanges = (resetForm) => {
        resetForm();
        navigate(-1);
    };

    const handleFormSubmit = async (values, { setSubmitting }) => {
        const data = new FormData();
        if (image && typeof image === "object") {
            data.append("image", image);
        }
        if (banner && typeof banner === "object") {
            data.append("banner", banner);
        }
        data.append("name", values.name);
        data.append("registration_number", values.registration_number);
        data.append("contact_information", values.contact_information);
        data.append("tax_id", values.tax_id);
        data.append("address", values.address);
        data.append("website", values.website);

        try {
            const response = rowData ? await patchManufacturer({ data: data, id: rowData.id, }) : await postManufacturer(data);

            if (response?.status === 201 || response?.status === 200) {
                toast.success(rowData ? "Manufacturer Updated Successfully" : "Manufacturer Added successfully!");
                navigate("/bazzar/manufacturer");
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
        setSubmitting(false);
    };

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: 'Bazzar', },
            { name: 'Manufacturers', },
            { name: rowData ? 'Edit Manufacturer' : 'Add Manufacturer' },
        ]} />
        <div className="mt-8">
            <Formik
                initialValues={rowData || {
                    name: "",
                    registration_number: "",
                    contact_information: "",
                    tax_id: "",
                    address: "",
                    website: "",
                }}
                enableReinitialize
                validationSchema={Yup.object({
                    name: Yup.string().required("Please enter a name"),
                    tax_id: Yup.string().required("Tax ID is required"),
                    registration_number: Yup.string().required("Reg. No. is required"),
                })}
                onSubmit={handleFormSubmit}
            >
                {({ isSubmitting, setFieldValue, resetForm, values }) => (
                    <Form>
                        <div className="border border-gray-300 mt-5 p-4 rounded-lg bg-gray-100 flex flex-col md:flex-row gap-4">
                            <div className="flex flex-col w-full">
                                <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                                    <InputField
                                        label="Name"
                                        name="name"
                                        placeholder="Manufacturer Name"
                                    />
                                    <InputField
                                        label="Registration Number"
                                        name="registration_number"
                                        type="text"
                                        placeholder="XXXXXXXXXXXX"
                                    />
                                    <InputField
                                        label={"Tax ID"}
                                        name="tax_id"
                                        placeholder={"Enter Tax ID"}
                                    />
                                    <InputField
                                        label={"Contact"}
                                        name="contact_information"
                                        placeholder={"XXXXXXXXXX"}

                                        onChange={(e) => setFieldValue('contact_information', sanitizeMobile(e.target.value))}
                                    />
                                </div>
                                <div className="grid gap-2 grid-cols-1">
                                    <InputField
                                        label="Website"
                                        name="website"
                                        type="url"
                                        placeholder="https://xyz.com"
                                        required
                                    />
                                    <InputField
                                        label="Address"
                                        name="address"
                                        placeholder="S1, 2nd Floor, Malwa Tower,..."
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col w-full bg-white rounded-lg p-4 justify-between">
                                <div className="flex flex-col">
                                    <StyledHeadingText text={"Images"} />
                                    <div className="flex flex-col gap-2 mt-4">
                                        <div className="h-48 w-48 aspect-square mx-2">
                                            <ImagePicker
                                                preloadedImage={image}
                                                onImageSelect={handleImageSelect}
                                                placeholder='Select Logo (1:1)'
                                            />
                                        </div>
                                        <div className="h-48 aspect-video mx-2">
                                            <ImagePicker
                                                preloadedImage={banner}
                                                onImageSelect={handleBannerSelect}
                                                placeholder='Select Banner Image (16:9)'
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="py-5 flex justify-end gap-6">
                                    <div>
                                        <Button
                                            type="button"
                                            className="text-primary bg-white hover:bg-white-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center border border-primary"
                                            onClick={() => handleDiscardChanges(resetForm)}
                                            disabled={isSubmitting}
                                        >
                                            Discard Changes
                                        </Button>
                                    </div>
                                    <div>
                                        <Button
                                            type="submit"
                                            className="text-white bg-primary hover:bg-primary-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? "Saving..." : "Save"}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    </>);
}

export default ManufacturerForm;